import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Package, MoreVertical, FileText, Clock, Search, ArrowUpDown } from 'lucide-react';
import { format } from '../utils/dateUtils';
import { Pagination } from '../components/Pagination';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

export const Releases = () => {
  const [releases, setReleases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 12;
  
  const [form, setForm] = useState({
    version: '',
    project_id: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (filterProject !== 'all') params.project_id = filterProject;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/releases/paginated`, { params });
      setReleases(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Failed to fetch releases:', error);
      toast.error('Failed to load releases');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterProject, filterStatus, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/releases`, form);
      toast.success('Release created');
      setDialogOpen(false);
      setForm({ version: '', project_id: '' });
      fetchReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create release');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this release?')) return;
    try {
      await axios.delete(`${API}/releases/${id}`);
      toast.success('Release deleted');
      fetchReleases();
    } catch (error) {
      toast.error('Failed to delete release');
    }
  };

  const handleStartRelease = async (id) => {
    try {
      await axios.post(`${API}/releases/${id}/start`);
      toast.success('Release started');
      fetchReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start release');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: 'bg-blue-100 text-blue-800',
      doing: 'bg-yellow-100 text-yellow-800',
      done: 'bg-green-100 text-green-800'
    };
    return (
      <Badge variant="secondary" className={styles[status] || styles.planned}>
        {status}
      </Badge>
    );
  };

  if (loading && releases.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="releases-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Releases</h1>
          <p className="text-muted-foreground mt-1">Manage software releases and release notes.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-release-btn">
              <Plus className="h-4 w-4" />
              New Release
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Release</DialogTitle>
              <DialogDescription>Add a new release version</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="project">Project *</Label>
                <Select
                  value={form.project_id ? String(form.project_id) : ''}
                  onValueChange={(value) => setForm({ ...form, project_id: parseInt(value) })}
                  required
                >
                  <SelectTrigger data-testid="release-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={String(project.id)}>
                        {project.prefix} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  value={form.version}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="e.g., 1.0.0"
                  required
                  data-testid="release-version-input"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                New releases start as "Planned". Use the User Stories page to start working on a release.
              </p>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="release-submit-btn">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by version..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
            data-testid="search-releases-input"
          />
        </div>
        <Select value={filterProject} onValueChange={(v) => { setFilterProject(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-48" data-testid="filter-release-project">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={String(project.id)}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-40" data-testid="filter-release-status">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="doing">Doing</SelectItem>
            <SelectItem value="done">Done</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 items-center">
          <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-36" data-testid="sort-by-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Date Added</SelectItem>
              <SelectItem value="version">Version</SelectItem>
              <SelectItem value="status">Status</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={(v) => { setSortOrder(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-28" data-testid="sort-order-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Newest</SelectItem>
              <SelectItem value="asc">Oldest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Releases Grid */}
      {releases.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="empty-state">
              <Package className="empty-state-icon" />
              <p className="empty-state-title">No releases yet</p>
              <p className="empty-state-description">
                Create your first release to start tracking versions.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {releases.map((release) => (
              <Card key={release.id} className="card-hover" data-testid={`release-card-${release.version}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono">
                        v{release.version}
                      </Badge>
                      {getStatusBadge(release.status)}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/releases/${release.id}`}>View Details</Link>
                        </DropdownMenuItem>
                        {release.status === 'planned' && (
                          <DropdownMenuItem onClick={() => handleStartRelease(release.id)}>
                            Start Release
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDelete(release.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardTitle className="text-lg mt-2">{release.project_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      {release.release_notes?.length || 0} notes
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(new Date(release.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};
